import { Loader } from "@/shared/ui/Loader";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { MegaphoneIcon, PlusIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useAnnouncementsQuery } from "../api/queries";
import { AnnouncementsFilter } from "../ui/AnnouncementsFilter";
import { AnnouncementsTable } from "../ui/AnnouncementsTable";

type AnnouncementsSearch = {
  isActive?: string;
  priority?: string;
  search?: string;
  offset?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
};

export function AnnouncementsPage() {
  const nav = useNavigate({ from: "/app/announcements" });
  const search = useSearch({ from: "/app/announcements" }) as AnnouncementsSearch;
  const canManage = useActionPermission(["announcements:update"]);

  const params = {
    isActive:
      search.isActive === "true"
        ? ("true" as const)
        : search.isActive === "false"
          ? ("false" as const)
          : undefined,
    priority:
      search.priority === "NORMAL" ||
      search.priority === "HIGH" ||
      search.priority === "URGENT"
        ? search.priority
        : undefined,
    search: search.search,
    offset: search.offset ?? 0,
    limit: search.limit ?? 20,
    sortField: (search.sortField ?? "publishAt") as
      | "title"
      | "publishAt"
      | "priority",
    sortOrder: (search.sortOrder ?? "desc") as "asc" | "desc",
  };

  const { data, isLoading } = useAnnouncementsQuery(params);

  const items = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ຂ່າວສານ",
              to: "/app/announcements",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <MegaphoneIcon className="size-5" />
              ຂ່າວສານ / ປະກາດ
            </h2>
            <p className="text-muted-foreground">
              ຈັດການຂ່າວສານແຈ້ງລູກຄ້າໃນແອັບ
            </p>
          </div>
          {canManage && (
            <Button onClick={() => nav({ to: "/app/announcements/create" })}>
              <PlusIcon className="size-4" />
              ສ້າງຂ່າວສານ
            </Button>
          )}
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <AnnouncementsFilter />

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <AnnouncementsTable
              data={items}
              isLoading={isLoading}
              offset={params.offset ?? 0}
              limit={params.limit ?? 20}
              totalCount={total}
              sortBy={params.sortField}
              sortOrder={params.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
              onEdit={(row) =>
                nav({
                  to: "/app/announcements/$announcementId/edit",
                  params: { announcementId: row.id },
                })
              }
            />
          )}
        </div>
      </Main>
    </>
  );
}
